import { CurrentUserContextProvider } from '@/modules/auth/providers/AuthUserProvider';
import { ErrorMessage } from '@/modules/common/components/ErrorMessage';
import { UseEducationProviderQueryOptions } from '@/modules/common/hooks/useEducationProviderOptions';
import { UseEmployerQueryOptions } from '@/modules/common/hooks/useEmployer';
import { getUseCurrentUserOptions } from '@/modules/common/hooks/useUser';
import { ICompany } from '@/types/company';
import { UserInclude, UserRole } from '@/types/user';
import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';

export const Route = createFileRoute('/_admin')({
  beforeLoad: async ({ location, context: { queryClient } }) => {
    const { data: user } = await queryClient.ensureQueryData(
      getUseCurrentUserOptions([
        UserInclude.Company,
        UserInclude.TaxBenefitProviders,
      ]),
    );

    if (!user) {
      throw redirect({
        to: '/logout',
        search: { redirectTo: encodeURIComponent(location.pathname) },
      });
    }

    if (user.educationProvider) {
      await queryClient.ensureQueryData(
        UseEducationProviderQueryOptions(user.educationProvider),
      );
    }

    if (
      ![
        UserRole.EducationProvider,
        UserRole.Employer,
        UserRole.TaxBenefitsProvider,
      ].some(role => user?.roles?.includes(role))
    ) {
      throw redirect({
        to: '/logout',
        search: { redirectTo: encodeURIComponent(location.pathname) },
      });
    }

    if (
      user?.roles.includes(UserRole.EducationProvider) &&
      !user.educationProvider
    ) {
      throw redirect({
        to: '/logout',
        search: { redirectTo: encodeURIComponent(location.pathname) },
      });
    }

    if (user?.roles.includes(UserRole.Employer)) {
      try {
        await queryClient.ensureQueryData(
          UseEmployerQueryOptions((user.company as ICompany)?._id as string),
        );
      } catch {
        throw redirect({
          to: '/logout',
          search: { redirectTo: encodeURIComponent(location.pathname) },
        });
      }
    }

    return {
      user,
    };
  },
  component: () => (
    <CurrentUserContextProvider>
      <Outlet />
    </CurrentUserContextProvider>
  ),
  errorComponent: () => {
    return <ErrorMessage error={{ statusCode: 500 }} reset={() => null} />;
  },
});
